import {
  Automation,
  AutomationActionTypes,
  AutomationTemplate,
  AutomationUpdateInput,
  GetListedAutomationsRequestData,
  ListedAutomationsListViewPage,
} from '@payaca/types/automationTypes';
import { action } from 'typesafe-actions';
import { AsyncActionCreator } from '../types';
import { createAsyncAction } from '../utils';
import {
  ActionType,
  CreateAutomationPayload,
  GetDefaultAutomationDefinition,
  GetDefaultAutomationDefinitions,
  UpdateDefaultAutomation,
} from './automationTypes';

export const requestGetListedAutomationsPage = (
  getListedAutomationsRequestData: GetListedAutomationsRequestData,
  callback?: (
    listedAutomationsListViewPage: ListedAutomationsListViewPage
  ) => void
) => {
  return action(ActionType.REQUEST_GET_LISTED_AUTOMATIONS_PAGE, {
    getListedAutomationsRequestData,
    callback,
  });
};

export const getListedAutomationsPageSuccess = (
  listedAutomationsListViewPage: ListedAutomationsListViewPage
) => {
  return action(
    ActionType.GET_LISTED_AUTOMATIONS_PAGE_SUCCESS,
    listedAutomationsListViewPage
  );
};

export const getListedAutomationsPageFailure = (error: Error) => {
  return action(
    ActionType.GET_LISTED_AUTOMATIONS_PAGE_FAILURE,
    null,
    null,
    error
  );
};

export const requestGetAutomation = (automationId: number) => {
  return action(ActionType.REQUEST_GET_AUTOMATION, { automationId });
};

export const getAutomationSuccess = (
  automationId: number,
  automation: Automation<AutomationActionTypes>
) => {
  return action(ActionType.GET_AUTOMATION_SUCCESS, {
    automationId,
    automation,
  });
};

export const getAutomationFailure = (automationId: number, error: Error) => {
  return action(ActionType.GET_AUTOMATION_FAILURE, automationId, null, error);
};

export const requestCreateAutomation = (
  createAutomationPayload: CreateAutomationPayload,
  callback?: (automationId: number) => void,
  onError?: (error: Error) => void
) => {
  return action(ActionType.REQUEST_CREATE_AUTOMATION, {
    createAutomationPayload,
    callback,
    onError,
  });
};

export const createAutomationSuccess = (automationId: number) => {
  return action(ActionType.CREATE_AUTOMATION_SUCCESS, automationId);
};

export const createAutomationFailure = (error: Error) => {
  return action(ActionType.CREATE_AUTOMATION_FAILURE, null, error);
};

export const requestUpdateAutomation = (
  automationId: number,
  automationUpdates: AutomationUpdateInput<AutomationActionTypes>,
  callback?: (automation: Automation<AutomationActionTypes>) => void,
  onError?: (error: Error) => void
) => {
  return action(ActionType.REQUEST_UPDATE_AUTOMATION, {
    automationId,
    automationUpdates,
    callback,
    onError,
  });
};

export const updateAutomationSuccess = (
  automationId: number,
  automation: Automation<AutomationActionTypes>
) => {
  return action(ActionType.UPDATE_AUTOMATION_SUCCESS, {
    automationId,
    automation,
  });
};

export const updateAutomationFailure = (automationId: number, error: Error) => {
  return action(
    ActionType.UPDATE_AUTOMATION_FAILURE,
    automationId,
    null,
    error
  );
};

export const requestArchiveAutomations = (
  automationIds: number[],
  onArchiveSuccess?: any
) => {
  return action(ActionType.REQUEST_ARCHIVE_AUTOMATIONS, {
    automationIds,
    onArchiveSuccess,
  });
};

export const archiveAutomationsSuccess = () => {
  return action(ActionType.ARCHIVE_AUTOMATIONS_SUCCESS);
};

export const archiveAutomationsFailure = (error: Error) => {
  return action(ActionType.ARCHIVE_AUTOMATIONS_FAILURE, null, error);
};

export const clearAutomations = () => {
  return action(ActionType.CLEAR_AUTOMATIONS);
};

export const requestGetAutomationTemplates = () => {
  return action(ActionType.REQUEST_GET_AUTOMATION_TEMPLATES);
};

export const getAutomationTemplatesSuccess = (
  automationTemplates: AutomationTemplate[]
) => {
  return action(ActionType.GET_AUTOMATION_TEMPLATES_SUCCESS, {
    automationTemplates,
  });
};

export const getAutomationTemplatesFailure = (error: Error) => {
  return action(ActionType.GET_AUTOMATION_TEMPLATES_FAILURE, null, error);
};

export const getDefaultAutomationDefinition: AsyncActionCreator<
  GetDefaultAutomationDefinition,
  (payload: GetDefaultAutomationDefinition['request']['payload']) => {
    payload: GetDefaultAutomationDefinition['request']['payload'];
  }
> = createAsyncAction<GetDefaultAutomationDefinition>({
  request: ActionType.GET_DEFAULT_AUTOMATION_DEFINITION_REQUEST,
})((payload: GetDefaultAutomationDefinition['request']['payload']) => ({
  payload,
}));

export const getDefaultAutomationDefinitions: AsyncActionCreator<
  GetDefaultAutomationDefinitions,
  (payload: GetDefaultAutomationDefinitions['request']['payload']) => {
    payload: GetDefaultAutomationDefinitions['request']['payload'];
  }
> = createAsyncAction<GetDefaultAutomationDefinitions>({
  request: ActionType.GET_DEFAULT_AUTOMATION_DEFINITIONS_REQUEST,
})((payload: GetDefaultAutomationDefinitions['request']['payload']) => ({
  payload,
}));

export const updateDefaultAutomation: AsyncActionCreator<
  UpdateDefaultAutomation,
  (payload: UpdateDefaultAutomation['request']['payload']) => {
    payload: UpdateDefaultAutomation['request']['payload'];
  }
> = createAsyncAction<UpdateDefaultAutomation>({
  request: ActionType.UPDATE_DEFAULT_AUTOMATION_REQUEST,
})((payload: UpdateDefaultAutomation['request']['payload']) => ({
  payload,
}));
